import React, { useEffect, useState } from "react";

import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { MdmObject, useDeleteObjectMutation, useGetCoordinatesLazyQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { Chips, ChipsChangeParams } from "primereact/chips";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { TabPanel, TabView } from "primereact/tabview";
import ObjectDevices from "./ObjectDevices";
import ObjectNotes from "./ObjectNotes";
import ObjectAnalytics from "./ObjectAnalytics";

type ObjectFormProps = {
  object: MdmObject | null;
  onSubmit: (object: Partial<MdmObject>) => void;
};
const emptyObject: Partial<MdmObject> = {
  tags: [],
};

const ObjectForm: React.FC<ObjectFormProps> = ({ object, onSubmit }) => {
  const [editable, setEditable] = useState(!object);
  const [objectState, setObjectState] = useState<Partial<MdmObject>>(object ?? emptyObject);
  const navigate = useNavigate();
  const [mutateFunction] = useDeleteObjectMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Object deleted successfully",
        life: 3000,
      });
      navigate("/");
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetObjectsByProject"],
  })

  const [getCoordinates, { data, loading, error }] = useGetCoordinatesLazyQuery();

  useEffect(() => {
    if (data && data.coordinates) {
      setObjectState(prev => ({
        ...prev,
        location: {
          ...prev.location,
          latitude: data.coordinates[0]?.latitude,
          longitude: data.coordinates[0]?.longitude,
        },
      }));
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setObjectState(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this object?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        if (object) {
          mutateFunction({ variables: { id: object.id! } });
        }
      },
    });
  };
 
  
  return (
    <div
      className="flex justify-content-between flex-column gap-5 mx-auto"
      style={{ maxWidth: "800px" }}
    >
      <ConfirmPopup />
      <div className="grid p-fluid">
        <InputLabelWrapper labelName="Object Code" col={4}>
          <InputText
            value={objectState.title ?? ""}
            onChange={handleChange}
            className="p-inputtext-sm"
            name="title"
            placeholder="Object Code"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="Description" col={6}>
          <InputText
            value={objectState.description ?? ""}
            onChange={handleChange}
            className="p-inputtext-sm"
            name="description"
            placeholder="Description"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <div className="text-right col-2 mt-2 ">
          {!editable && (
            <Button
              icon="pi pi-pencil"
              className="p-button-rounded p-button-text"
              onClick={() => setEditable(true)}
            />
          )}
          {editable && object && (
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-text p-button-danger"
              onClick={handleDelete}
            />
          )}
        </div>
        <InputLabelWrapper labelName="Country" col={2}>
          <InputText
            value={objectState.address?.country ?? ""}
            onChange={handleAddressChange}
            className="p-inputtext-sm"
            name="country"
            placeholder="Country"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="City" col={3}>
          <InputText
            value={objectState.address?.city ?? ""}
            onChange={handleAddressChange}
            className="p-inputtext-sm"
            name="city"
            placeholder="City"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="Street" col={3}>
          <InputText
            value={objectState.address?.street ?? ""}
            onChange={handleAddressChange}
            className="p-inputtext-sm"
            name="street"
            placeholder="Street"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="House number" col={2}>
          <InputText
            value={objectState.address?.houseNumber ?? ""}
            onChange={handleAddressChange}
            className="p-inputtext-sm"
            name="houseNumber"
            placeholder="House number"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="Post code" col={2}>
          <InputText
            value={objectState.address?.postCode ?? ""}
            onChange={handleAddressChange}
            className="p-inputtext-sm"
            name="postCode"
            placeholder="Post code"
            disabled={!editable}
          />
        </InputLabelWrapper>
        <InputLabelWrapper labelName="Latitude/Longitude">
          <div className="p-inputgroup">
            <Button
              loading={loading}
              label="Get coordinates"
              onClick={() =>
                getCoordinates({
                  variables: { address: objectState.address! },
                })
              }
              disabled={!editable}
            />
            <InputNumber
              value={objectState.location?.latitude}
              onChange={e =>
                setObjectState(prev => ({
                  ...prev,
                  location: { ...prev.location, latitude: e.value },
                }))
              }
              className="p-inputtext-sm"
              name="latitude"
              placeholder="Latitude"
              useGrouping={false}
              maxFractionDigits={10}
              disabled={!editable}
            />
            <InputNumber
              value={objectState.location?.longitude}
              onChange={e =>
                setObjectState(prev => ({
                  ...prev,
                  location: { ...prev.location, longitude: e.value },
                }))
              }
              className="p-inputtext-sm"
              name="longitude"
              placeholder="Longitude"
              useGrouping={false}
              maxFractionDigits={10}
              disabled={!editable}
            />
          </div>
        </InputLabelWrapper>
        {error && <div className="flex align-items-end col-6 text-red-500">{error.message}</div>}
        <InputLabelWrapper labelName="Tags" col={12}>
          <Chips
            value={objectState.tags ?? []}
            onChange={handleChange}
            className="p-inputtext-sm"
            name="tags"
            placeholder="Tags.."
            disabled={!editable}
          />
        </InputLabelWrapper>
        {object && (
          <TabView className="col-12">
            <TabPanel header="Devices">
              <ObjectDevices object={object} />
            </TabPanel>
            <TabPanel header="Analytics">
              <ObjectAnalytics object={object} />
            </TabPanel>
            <TabPanel header="Notes">
              <ObjectNotes object={object} />
            </TabPanel>
          </TabView>
        )}
      </div>

      <div className="flex w-full justify-content-end gap-3">
        {editable && (
          <Button
            onClick={() => onSubmit(objectState)}
            label={object ? "Update" : "Create"}
            className={`p-button-rounded p-button-${object ? "primary" : "success"} w-10rem`}
          />
        )}
        {object && editable && (
          <Button
            onClick={() => {
              setEditable(false);
              setObjectState(object ?? emptyObject);
            }}
            label="Cancel"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
        {(!object || !editable) && (
          <Button
            onClick={() => navigate(-1)}
            label="Back"
            className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
          />
        )}
      </div>
    </div>
  );
};

export default ObjectForm;
